import React, { useCallback, useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Stack,
  Slide,
  Icon,
  TableContainer,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Divider,
  Tooltip,
  DialogTitle,
  Dialog,
  DialogContent,
  Card
} from 'investira.react.components';

import { displays } from 'investira.react.lib';
import { formats, validators } from 'investira.sdk';
import { PessoaImage } from '../../../../../components/molecules';
import { ATIVO_TIPO, CONSOLIDACAO_TIPO } from '@investirapri/financa.js/lib/enums';

const commonColumns = {
  pessoa: { key: 'pessoa', label: '', align: 'left' },
  pessoa_fantasia: { key: 'pessoa_fantasia', label: 'Fundo', align: 'left' },
  ativo: { key: 'ativo', label: 'Ativo', align: 'left' },
  ticker: { key: 'ticker', label: 'Ticker', align: 'left' },
  cnpj: { key: 'cnpj', label: 'CNPJ', align: 'left' },
  isin: { key: 'isin', label: 'ISIN', align: 'left' },
  emissor: { key: 'emissor', label: 'Emissor', align: 'left' },
  financeiro: { key: 'financeiro', label: 'Financeiro', align: 'right' },
  vencimento: { key: 'vencimento', label: 'Vencimento', align: 'left' },
  codigo: { key: 'codigo', label: 'Código', align: 'left' },
  ativo_tipo: { key: 'ativo_tipo', label: 'Tipo de Ativo', align: 'left' },
  serie: { key: 'serie', label: 'Série', align: 'left' },
  curva: { key: 'curva_id', label: 'Curva', align: 'left' }
};

const commonColumnsCarteira = {
  carteira: { key: 'carteira', label: 'Carteira', align: 'left', getValue: item => item.carteira },
  carteira_seuid: {
    key: 'carteira_seuid',
    label: 'Carteira SeuID',
    align: 'left',
    getValue: item => item.carteira_seuid
  },
  caixa: {
    key: 'caixa',
    label: 'Caixa Proporcional',
    align: 'right',
    getValue: item => item.contabil?.caixa || null
  },
  provisoes: {
    key: 'provisoes',
    label: 'Provisões Proporcionais',
    align: 'right',
    getValue: item => item.contabil?.provisoes || null
  },
  // fator: { key: 'fator', label: 'Fator', align: 'right', getValue: item => item.fator },
  fator_proporcional: {
    key: 'fator_proporcional',
    label: 'Fator Proporcional',
    align: 'right',
    getValue: item => item.fator_proporcional
  },
  financeiro: {
    key: 'financeiro',
    label: 'Financeiro Proporcional',
    align: 'right',
    getValue: item => item.financeiro
  }
};

const columns = [
  commonColumns.pessoa,
  commonColumns.pessoa_fantasia,
  commonColumns.isin,
  commonColumns.cnpj
];

const columnsCarteira = [
  commonColumnsCarteira.carteira,
  commonColumnsCarteira.carteira_seuid,
  commonColumnsCarteira.financeiro,
  commonColumnsCarteira.fator_proporcional,
  commonColumnsCarteira.caixa,
  commonColumnsCarteira.provisoes
];

const labels = {
  base: 'Base',
  data: 'Data',
  tipo: 'Tipo',
  consolidacao_tipo: 'Tipo de Consolidação',
  valor: 'Valor',
  financeiro: 'Financeiro',
  rota: 'Rota',
  nivel: 'Nível da Rota',
  arvore: 'Árvore da Rota',
  fator: 'Fator',
  aberta: 'Aberta',
  contabil: '',
  caixa: 'Caixa',
  provisoes: 'Provisões',
  posicao_direta: 'Posição Direta',
  // financeiro: 'Posição Direta Financeiro',
  quantidade: 'Quantidade',
  fator_proporcional: 'Fator Proporcional',
  proporcional: 'Proporcional'
};

const FundosAbertosDetalhe = props => {
  const { data, open, setOpen } = props;
  const [selectedItem, setSelectedItem] = useState(null);
  const [showList, setShowList] = useState(true);
  const [showDetail, setShowDetail] = useState(false);
  const [showDetailPosicaoDireta, setShowDetailPosicaoDireta] = useState(false);

  console.log('props', props);

  const handleItemClick = useCallback(
    (item, isPosicaoDireta = false) => {
      const xDeepCopyItem = JSON.parse(JSON.stringify(item));
      delete xDeepCopyItem.participacao.aberta;
      setSelectedItem(xDeepCopyItem);
      // Pequeno delay para a animação ser mais fluida
      setTimeout(() => {
        setShowList(false);
        if (isPosicaoDireta) {
          setShowDetail(false);
          setShowDetailPosicaoDireta(true);
        } else {
          setShowDetail(true);
        }
      }, 100);
    },
    [data]
  );

  const handleBackClick = (isPosicaoDireta = false) => {
    // Pequeno delay para a animação ser mais fluida
    setTimeout(() => {
      if (isPosicaoDireta) {
        setShowDetailPosicaoDireta(false);
        setShowDetail(true);
      } else {
        setShowDetail(false);
        setShowList(true);
        setSelectedItem(null);
      }
    }, 100);
  };

  const handleCloseClick = () => {
    setOpen(false);
    setSelectedItem(null);
  };

  const getArvoreValues = pValue => {
    const xValues = pValue.split(',');
    if (xValues.length === 0) {
      return '';
    }
    if (xValues.length === 1) {
      return xValues[0];
    }
    return (
      <Stack direction="row" justifyContent="flex-end" gap={1}>
        {xValues.map((value, index) => {
          const xFundo = data.response?.resumo.fundos_abertos?.find(pItem => pItem.isin === value);
          return (
            <Stack key={index} component="span" direction="row" gap={1}>
              {value}
              {index < xValues.length - 1 && (
                <>
                  <Tooltip
                    title={
                      <Typography color="inherit" variant="caption" component="p" gutterBottom>
                        <Stack direction="row" alignItems="center" gap={1}>
                          <PessoaImage
                            logoPessoaId={xFundo?.logo_pessoa_id}
                            alt={xFundo?.gestor?.pessoa_fantasia}
                            hasLogo={Boolean(xFundo?.logo_pessoa_id)}
                            size={24}
                          />
                          {xFundo?.pessoa_fantasia || ''}
                        </Stack>
                      </Typography>
                    }>
                    <span style={{ marginTop: '2px' }}>
                      <Icon size={16} color={'primary'} iconName="information" />
                    </span>
                  </Tooltip>
                  {' > '}
                </>
              )}
            </Stack>
          );
        })}
      </Stack>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseClick}
      maxWidth={showDetailPosicaoDireta ? 'xl' : 'lg'}
      fullWidth={showDetailPosicaoDireta}>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          {showDetailPosicaoDireta ? 'Posição Direta' : 'Fundos Abertos'}
          <IconButton color="primary" onClick={handleCloseClick}>
            <Icon iconName="cancel" size={21} />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ display: 'flex' }}>
        <Box
          sx={{
            width: '100%',
            mx: 'auto',
            position: 'relative',
            overflow: 'hidden',
            pb: 2,
            transition: 'all 0.1s ease-in-out'
          }}>
          <Slide
            key={`list-${showList}`}
            direction="right"
            in={showList}
            mountOnEnter
            unmountOnExit>
            <Stack sx={{ height: '100%' }}>
              <TableContainer
                id="table-container"
                sx={{
                  overflow: 'auto'
                }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {columns.map(column => (
                        <TableCell key={column.key} align={column.align}>
                          {column.label}
                        </TableCell>
                      ))}
                      <TableCell>Obs.</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.response?.resumo.fundos_abertos &&
                      data.response?.resumo.fundos_abertos.map((xItem, xIndex) => {
                        return (
                          <TableRow key={xItem.isin}>
                            {columns.map(column => {
                              if (column.key === 'pessoa') {
                                return (
                                  <TableCell key={column.key} sx={{ width: '24px' }}>
                                    <PessoaImage
                                      logoPessoaId={xItem.logo_pessoa_id}
                                      alt={xItem.gestor.pessoa_fantasia}
                                      hasLogo={Boolean(xItem?.logo_pessoa_id)}
                                      size={24}
                                    />
                                  </TableCell>
                                );
                              }

                              const value = xItem[column.key];

                              if (column.key === 'ativo') {
                                return (
                                  <TableCell
                                    key={column.key}
                                    sx={{
                                      width: '24px',
                                      whiteSpace: 'nowrap'
                                    }}>
                                    {value}
                                  </TableCell>
                                );
                              }

                              if (column.key === 'cnpj') {
                                return (
                                  <TableCell
                                    key={column.key}
                                    sx={{
                                      width: '24px',
                                      whiteSpace: 'nowrap'
                                    }}
                                    align="right">
                                    {displays.cnpj(value)}
                                  </TableCell>
                                );
                              }

                              const isDate = ['operacao_data', 'emissao', 'vencimento'].includes(
                                column.key
                              );

                              const isNumber = ['quantidade', 'pu', 'financeiro', 'taxa'].includes(
                                column.key
                              );

                              return (
                                <TableCell key={column.key} align={column.align}>
                                  {isNumber && value
                                    ? formats.formatNumber(value, 2, true)
                                    : isDate && value
                                    ? formats.formatDateCustom(value, 'DD/MM/YYYY')
                                    : value}

                                  {}
                                </TableCell>
                              );
                            })}
                            <TableCell key={`next-${xIndex}`} sx={{ width: '24px' }} align="center">
                              {xItem.participacao.fator > 1 && (
                                <Tooltip title="Possível inconsistência">
                                  <span>
                                    <Icon iconName="warning" size={16} color="warn" />
                                  </span>
                                </Tooltip>
                              )}
                            </TableCell>
                            <TableCell key={`next-${xIndex}`} sx={{ width: '24px' }}>
                              <IconButton
                                onClick={() => handleItemClick(xItem)}
                                edge="start"
                                //sx={{ mr: 1 }}
                                color="primary">
                                <Icon iconName="arrow-next" size={16} />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Stack>
          </Slide>

          <Slide
            key={`detail-${showDetail}`}
            direction="left"
            in={showDetail}
            mountOnEnter
            unmountOnExit>
            <Stack
              sx={{
                height: '100%',
                width: '100%'
              }}>
              {selectedItem && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, px: 1.5 }}>
                    <IconButton
                      onClick={() => handleBackClick(false)}
                      edge="start"
                      sx={{ mr: 1 }}
                      color="primary">
                      <Icon iconName="arrow-previous" size={18} />
                    </IconButton>
                  </Box>
                  <Divider />
                  <Box sx={{ overflow: 'auto', maxHeight: 600 }}>
                    <Stack py={2}>
                      <Stack mb={1}>
                        <PessoaImage
                          logoPessoaId={selectedItem.logo_pessoa_id}
                          alt={selectedItem.gestor.pessoa_fantasia}
                          hasLogo={Boolean(selectedItem?.logo_pessoa_id)}
                          size={50}
                        />
                      </Stack>
                      <Typography variant="subtitle1">
                        <b>{selectedItem.pessoa_fantasia}</b>
                      </Typography>
                    </Stack>
                    <Divider />

                    <Stack py={1}>
                      <Typography variant="caption" color={'textSecondary'}>
                        {ATIVO_TIPO[selectedItem.ativo_tipo_id].descricao}
                      </Typography>
                    </Stack>
                    <Divider />
                    <Stack py={1}>
                      {selectedItem.participacao && (
                        <>
                          {Object.entries(selectedItem.participacao)
                            .sort()
                            .map(([key, value]) => (
                              <React.Fragment key={key}>
                                {key === 'aberta' ||
                                  (labels[key] !== '' && (
                                    <Typography variant="body2" color="text.primary" sx={{ mb: 1 }}>
                                      <Stack
                                        display="flex"
                                        flexDirection="row"
                                        alignItems="center"
                                        justifyContent="space-between">
                                        <b>{key === 'aberta' ? 'Posição' : labels[key]}</b>
                                        {key === 'posicao_direta' &&
                                          Object.values(selectedItem?.carteiras || {}).length >
                                            1 && (
                                            <IconButton
                                              onClick={() => handleItemClick(selectedItem, true)}
                                              edge="start"
                                              //sx={{ mr: 1 }}
                                              color="primary">
                                              <Icon iconName="arrow-next" size={16} />
                                            </IconButton>
                                          )}
                                      </Stack>
                                    </Typography>
                                  ))}
                                <Stack mb={2}>
                                  <TableContainer>
                                    <Table size="small">
                                      <TableBody>
                                        {typeof value === 'object' ? (
                                          Object.entries(value)
                                            // .sort()
                                            .map(([subKey, subValue]) =>
                                              subKey === 'contabil' ? (
                                                Object.entries(subValue).map(
                                                  ([subSubKey, subSubValue]) => (
                                                    <TableRow key={subSubKey}>
                                                      <TableCell>{labels[subSubKey]}</TableCell>
                                                      <TableCell align="right">
                                                        {formats.formatNumber(subSubValue, 2, true)}
                                                      </TableCell>
                                                    </TableRow>
                                                  )
                                                )
                                              ) : validators.isNull(subValue) ? (
                                                <></>
                                              ) : (
                                                <TableRow key={subKey}>
                                                  <TableCell>{labels[subKey]}</TableCell>
                                                  <TableCell align="right">
                                                    {subKey === 'arvore'
                                                      ? getArvoreValues(subValue)
                                                      : subKey === 'data'
                                                      ? (subValue &&
                                                          formats.formatDate(
                                                            subValue,
                                                            'DD/MM/YYYY'
                                                          )) ||
                                                        '--'
                                                      : typeof subValue === 'boolean'
                                                      ? subValue
                                                        ? 'Sim'
                                                        : 'Não'
                                                      : subKey === 'consolidacao_tipo'
                                                      ? Object.values(CONSOLIDACAO_TIPO).find(
                                                          xItem => xItem.id === subValue
                                                        )?.descricao || ''
                                                      : subKey === 'nivel'
                                                      ? subValue
                                                      : subKey === 'fator'
                                                      ? formats.formatNumber(subValue, 15)
                                                      : subKey === 'financeiro'
                                                      ? formats.formatNumber(subValue, 2, true)
                                                      : subValue.toLocaleString('pt-BR', {
                                                          minimumFractionDigits: 2,
                                                          maximumFractionDigits: 2
                                                        })}
                                                  </TableCell>
                                                </TableRow>
                                              )
                                            )
                                        ) : validators.isNull(value) ? (
                                          <></>
                                        ) : (
                                          <TableRow>
                                            <TableCell>{labels[key]}</TableCell>
                                            <TableCell align="right">
                                              {key === 'arvore' ? (
                                                getArvoreValues(value)
                                              ) : key === 'data' ? (
                                                (value &&
                                                  formats.formatDate(value, 'DD/MM/YYYY')) ||
                                                '--'
                                              ) : typeof value === 'boolean' ? (
                                                value ? (
                                                  'Sim'
                                                ) : (
                                                  'Não'
                                                )
                                              ) : key === 'consolidacao_tipo' ? (
                                                Object.values(CONSOLIDACAO_TIPO).find(
                                                  xItem => xItem.id === value
                                                )?.descricao || ''
                                              ) : key === 'nivel' ? (
                                                value
                                              ) : ['fator'].includes(key) ? (
                                                <Stack
                                                  gap={2}
                                                  justifyContent="flex-end"
                                                  direction="row">
                                                  {value && formats.formatNumber(value, 15)}
                                                  {value > 1 && (
                                                    <Tooltip title="Posição na carteira está superior ao patrimônio líquido">
                                                      <span>
                                                        <Icon
                                                          iconName="warning"
                                                          size={16}
                                                          color="warn"
                                                        />
                                                      </span>
                                                    </Tooltip>
                                                  )}
                                                </Stack>
                                              ) : (
                                                // ) : key === 'financeiro' ? (
                                                //   formats.formatNumber(value, 2, true)
                                                value.toLocaleString('pt-BR', {
                                                  minimumFractionDigits: 2,
                                                  maximumFractionDigits: 2
                                                })
                                              )}
                                            </TableCell>
                                          </TableRow>
                                        )}
                                      </TableBody>
                                    </Table>
                                  </TableContainer>
                                </Stack>
                              </React.Fragment>
                            ))}
                        </>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        Gestor: {selectedItem.gestor.pessoa_fantasia} (
                        {displays.cnpj(selectedItem.gestor.cnpj)})
                      </Typography>
                    </Stack>
                  </Box>
                </>
              )}
            </Stack>
          </Slide>

          <Slide
            key={`detail-posicao-direta-${showDetailPosicaoDireta}`}
            direction="left"
            in={showDetailPosicaoDireta}
            mountOnEnter
            unmountOnExit>
            <Stack
              sx={{
                height: '100%',
                width: '100%'
              }}>
              {selectedItem && (
                <>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      mb: 1,
                      px: 1.5
                    }}>
                    <IconButton
                      onClick={() => handleBackClick(true)}
                      edge="start"
                      sx={{ mr: 1 }}
                      color="primary">
                      <Icon iconName="arrow-previous" size={18} />
                    </IconButton>
                  </Box>
                  <Divider />
                  <Box sx={{ overflow: 'auto', maxHeight: 600 }}>
                    <Stack
                      sx={{ gap: 1, flexGrow: 1, py: 2 }}
                      direction={{ xs: 'column', md: 'row' }}>
                      <Card
                        variant="outlined"
                        sx={{
                          display: 'flex',
                          flex: 1
                        }}>
                        <Stack px={2} py={2} flexGrow={1} justifyContent="space-between">
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 500 }}>
                            Financeiro
                          </Typography>
                          <Stack direction="column" justifyContent="flex-end">
                            <Stack>
                              <Typography variant="h5">
                                <b>
                                  {selectedItem.participacao?.posicao_direta?.financeiro
                                    ? formats.formatNumber(
                                        selectedItem.participacao.posicao_direta.financeiro,
                                        2,
                                        true
                                      )
                                    : '--'}
                                </b>
                              </Typography>
                            </Stack>
                          </Stack>
                        </Stack>
                      </Card>
                      <Card
                        variant="outlined"
                        sx={{
                          display: 'flex',
                          flex: 1
                        }}>
                        <Stack px={2} py={2} flexGrow={1} justifyContent="space-between">
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 500 }}>
                            Fator
                          </Typography>
                          <Stack direction="column" justifyContent="flex-end">
                            <Stack>
                              <Typography variant="h5">
                                <b>
                                  {selectedItem.participacao?.posicao_direta?.fator
                                    ? formats.formatNumber(
                                        selectedItem.participacao.posicao_direta.fator,
                                        15
                                      )
                                    : '--'}
                                </b>
                              </Typography>
                            </Stack>
                          </Stack>
                        </Stack>
                      </Card>
                      <Card
                        variant="outlined"
                        sx={{
                          display: 'flex',
                          flex: 1
                        }}>
                        <Stack px={2} py={2} flexGrow={1} justifyContent="space-between">
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 500 }}>
                            Caixa
                          </Typography>
                          <Stack direction="column" justifyContent="flex-end">
                            <Stack>
                              <Typography variant="h5">
                                <b>
                                  {selectedItem.participacao?.posicao_direta?.contabil?.caixa
                                    ? formats.formatNumber(
                                        selectedItem.participacao.posicao_direta.contabil.caixa,
                                        2,
                                        true
                                      )
                                    : '--'}
                                </b>
                              </Typography>
                            </Stack>
                          </Stack>
                        </Stack>
                      </Card>
                      <Card
                        variant="outlined"
                        sx={{
                          display: 'flex',
                          flex: 1
                        }}>
                        <Stack px={2} py={2} flexGrow={1} justifyContent="space-between">
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ fontWeight: 500 }}>
                            Provisões
                          </Typography>
                          <Stack direction="column" justifyContent="flex-end">
                            <Stack>
                              <Typography variant="h5">
                                <b>
                                  {selectedItem.participacao?.posicao_direta?.contabil?.provisoes
                                    ? formats.formatNumber(
                                        selectedItem.participacao.posicao_direta.contabil.provisoes,
                                        2,
                                        true
                                      )
                                    : '--'}
                                </b>
                              </Typography>
                            </Stack>
                          </Stack>
                        </Stack>
                      </Card>
                    </Stack>
                    <Divider />
                    <Stack py={1}>
                      <Stack py={1}>
                        <Typography variant="body2" color="text.primary" sx={{ mb: 1 }}>
                          <b>Contábil</b>
                        </Typography>
                      </Stack>
                      <Stack mb={2}>
                        {selectedItem.carteiras && (
                          <TableContainer
                            id="table-container"
                            sx={{
                              overflow: 'auto'
                            }}>
                            <Table size="small">
                              <TableHead>
                                <TableRow>
                                  {columnsCarteira.map(column => (
                                    <TableCell key={column.key} align={column.align}>
                                      {column.label}
                                    </TableCell>
                                  ))}
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {Object.values(selectedItem.carteiras)
                                  .sort((a, b) => {
                                    // Aplica ordenação pela data mais recente
                                    return new Date(b.data) - new Date(a.data);
                                  })
                                  .map(carteira => (
                                    <TableRow key={carteira.carteira_seuid}>
                                      {columnsCarteira.map(column => {
                                        const value = column.getValue(carteira);

                                        if (['fator_proporcional'].includes(column.key)) {
                                          return (
                                            <TableCell key={column.key} align={column.align}>
                                              <Stack
                                                gap={2}
                                                justifyContent="flex-end"
                                                direction="row">
                                                {formats.formatNumber(value, 15)}
                                                {value > 1 && (
                                                  <Tooltip title="Posição na carteira está superior ao patrimônio líquido">
                                                    <span>
                                                      <Icon
                                                        iconName="warning"
                                                        size={16}
                                                        color="warn"
                                                      />
                                                    </span>
                                                  </Tooltip>
                                                )}
                                              </Stack>
                                            </TableCell>
                                          );
                                        }

                                        if (
                                          ['caixa', 'provisoes', 'financeiro'].includes(column.key)
                                        ) {
                                          return (
                                            <TableCell key={column.key} align={column.align}>
                                              {value ? formats.formatNumber(value, 2, true) : '--'}
                                            </TableCell>
                                          );
                                        }

                                        return (
                                          <TableCell key={column.key} align={column.align}>
                                            {value}
                                          </TableCell>
                                        );
                                      })}
                                    </TableRow>
                                  ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                        )}
                      </Stack>
                    </Stack>
                  </Box>
                </>
              )}
            </Stack>
          </Slide>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default FundosAbertosDetalhe;
